import { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useGetV1Plants } from "@solarkey/api-client";
import { useCapabilities } from "../session/capabilities";

interface TabDef {
  key: string;
  label: string;
  segment: string;
  built: boolean;
  entitlement?: string;
  tierLabel?: string;
  /** Tiers at which this item is locked (shown greyed with a tier badge, does not open). */
  lockedAt?: string[];
}

const MAIN_TABS: TabDef[] = [
  { key: "now", label: "Now", segment: "now", built: true },
  { key: "performance", label: "Performance", segment: "performance", built: true },
  { key: "blocks", label: "Blocks", segment: "blocks", built: true },
  { key: "sensors", label: "Meters and sensors", segment: "sensors", built: true },
  // UX-REV-002 N1: the signal explorer (plants/$site/explorer) was reachable only
  // via an in-page button three clicks deep. Surface it directly in the tab bar
  // next to Meters and sensors — it is the plant's primary diagnosis tool.
  { key: "explorer", label: "Explorer", segment: "explorer", built: true },
  { key: "alerts", label: "Alerts", segment: "alerts", built: true },
  { key: "equipment", label: "Equipment", segment: "equipment", built: true },
];

const MORE_ITEMS: TabDef[] = [
  { key: "strings", label: "Strings and combiners", segment: "strings", built: true, lockedAt: ["easy_m"], tierLabel: "Easy T" },
  { key: "schematic", label: "Schematic", segment: "schematic", built: true, lockedAt: ["easy_m"], tierLabel: "Easy T" },
  { key: "weather", label: "Weather and forecast", segment: "weather", built: true },
  { key: "datapath", label: "Data path and coverage", segment: "datapath", built: true },
  { key: "settings", label: "Settings", segment: "settings", built: true },
];

const ALL_SEGMENTS = new Set([
  ...MAIN_TABS.map((t) => t.segment),
  ...MORE_ITEMS.map((t) => t.segment),
]);

function parseFromPath(pathname: string): { basePath: string; activeSegment: string | null } {
  const match = pathname.match(/^(.*\/plants\/[^/]+)/);
  const base = match?.[1] ?? "";
  const rest = base ? pathname.slice(base.length) : "";
  const parts = rest.split("/").filter(Boolean);
  const seg = parts.length > 0 ? parts[0] : null;
  return { basePath: base, activeSegment: seg && ALL_SEGMENTS.has(seg) ? seg : null };
}

interface SiteTabBarProps {
  site: string;
  alertCount?: number;
}

export function SiteTabBar({ alertCount }: SiteTabBarProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { basePath, activeSegment } = parseFromPath(pathname);
  const isMoreActive = MORE_ITEMS.some((t) => t.segment === activeSegment);
  const navigate = useNavigate();

  const caps = useCapabilities();

  // Sibling-site pagination (wireframe: "N of M in <scope> · <next> ›").
  const route = pathname.match(/^\/t\/([^/]+)\/s\/([^/]+)\/plants\/([^/]+)/);
  const tenant = route?.[1] ?? "";
  const scope = route?.[2] ?? "";
  const currentSite = route?.[3] ?? "";
  const plantsQuery = useGetV1Plants(
    { period: "today", scope } as Parameters<typeof useGetV1Plants>[0],
    { query: { staleTime: 60_000, enabled: !!scope } },
  );
  const siteIds: string[] = plantsQuery.data?.status === 200
    ? (plantsQuery.data.data.data.items ?? []).map((i) => i.site_id ?? "").filter(Boolean)
    : [];
  const siteIdx = siteIds.indexOf(currentSite);
  const scopeLabel = caps.scopes.find((s) => s.id === scope)?.label ?? scope;
  const prevSite = siteIdx > 0 ? siteIds[siteIdx - 1] : undefined;
  const nextSite = siteIdx >= 0 && siteIdx < siteIds.length - 1 ? siteIds[siteIdx + 1] : undefined;
  const goToSite = (s?: string) => {
    if (!s) return;
    const seg = activeSegment ?? "now";
    void navigate({ to: `/t/${tenant}/s/${scope}/plants/${s}/${seg}` as never });
  };
  const tierLock: Record<string, boolean> = {};
  for (const item of MORE_ITEMS) {
    if (item.lockedAt?.includes(caps.tier)) {
      tierLock[item.key] = true;
    } else if (item.entitlement && caps.entitlements[item.entitlement] === false) {
      tierLock[item.key] = true;
    }
  }

  return (
    <nav aria-label="Site" style={barStyle}>
      <div style={tabsRow}>
        {MAIN_TABS.map((tab) => (
          <SiteTab
            key={tab.key}
            tab={tab}
            basePath={basePath}
            active={tab.segment === activeSegment}
            alertCount={tab.key === "alerts" ? alertCount : undefined}
            locked={false}
          />
        ))}
        <MoreMenu
          items={MORE_ITEMS}
          basePath={basePath}
          active={isMoreActive}
          current={activeSegment}
          tierLock={tierLock}
        />
      </div>
      <div style={navSiteRow}>
        {siteIdx >= 0 && siteIds.length > 0 ? (
          <span style={siteNavPos}>{siteIdx + 1} of {siteIds.length} in {scopeLabel}</span>
        ) : null}
        <button
          type="button"
          style={siteNavBtn}
          disabled={!prevSite}
          onClick={() => goToSite(prevSite)}
          title={prevSite ? `Previous site: ${prevSite}` : "Previous site"}
        >
          &#8249; Prev
        </button>
        <button
          type="button"
          style={siteNavBtn}
          disabled={!nextSite}
          onClick={() => goToSite(nextSite)}
          title={nextSite ? `Next site: ${nextSite}` : "Next site"}
        >
          {nextSite ?? "Next"} &#8250;
        </button>
      </div>
    </nav>
  );
}

function SiteTab({
  tab,
  basePath,
  active,
  alertCount,
  locked,
}: {
  tab: TabDef;
  basePath: string;
  active: boolean;
  alertCount?: number;
  locked: boolean;
}) {
  if (locked) {
    return (
      <span style={{ ...tabBase, ...lockedStyle }} aria-disabled="true">
        {tab.label}
        {tab.tierLabel ? <span style={tierChip}>{tab.tierLabel}</span> : null}
      </span>
    );
  }
  if (!tab.built) {
    return (
      <span style={{ ...tabBase, ...laterStyle }} aria-disabled="true">
        {tab.label} <span style={laterMark}>(later)</span>
      </span>
    );
  }
  const href = `${basePath}/${tab.segment}`;
  return (
    <Link
      to={href as never}
      style={{ ...tabBase, ...(active ? activeStyle : inactiveStyle) }}
    >
      {tab.label}
      {alertCount != null && alertCount > 0 ? (
        <span style={countBadge}>[{alertCount}]</span>
      ) : null}
    </Link>
  );
}

function MoreMenu({
  items,
  basePath,
  active,
  current,
  tierLock,
}: {
  items: TabDef[];
  basePath: string;
  active: boolean;
  current: string | null;
  tierLock: Record<string, boolean>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          btnRef.current && !btnRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  function handleToggle() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left });
    }
    setOpen((v) => !v);
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        style={{ ...tabBase, ...(active ? activeStyle : inactiveStyle), cursor: "pointer", background: "none", border: "none" }}
        onClick={handleToggle}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        More &#9662;
      </button>
      {open ? (
        <div ref={ref} style={{ ...menuStyle, position: "fixed", top: pos.top, left: pos.left }} role="menu">
          {items.map((item) => {
            const isLocked = tierLock[item.key] ?? false;
            const isCurrent = item.segment === current;
            if (isLocked) {
              return (
                <span key={item.key} role="menuitem" aria-disabled="true" style={{ ...menuItemStyle, ...lockedStyle }}>
                  {item.label}
                  {item.tierLabel ? <span style={tierChip}>{item.tierLabel}</span> : null}
                </span>
              );
            }
            if (!item.built) {
              return (
                <span key={item.key} role="menuitem" aria-disabled="true" style={{ ...menuItemStyle, ...laterStyle }}>
                  {item.label} <span style={laterMark}>(later)</span>
                </span>
              );
            }
            const href = `${basePath}/${item.segment}`;
            return (
              <a
                key={item.key}
                role="menuitem"
                href={href}
                style={{ ...menuItemStyle, ...(isCurrent ? menuItemActiveStyle : {}) }}
                onClick={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  void navigate({ to: href as never });
                }}
              >
                {item.label}
                {isCurrent ? <span style={{ marginLeft: "auto", fontSize: 11 }}>&#9679;</span> : null}
              </a>
            );
          })}
        </div>
      ) : null}
    </>
  );
}

const barStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: "1px solid var(--sk-color-border, #e0e0e0)",
  margin: "0 -20px",
  padding: "0 20px",
  background: "var(--sk-color-surface-1, #fff)",
  gap: 8,
  flexWrap: "wrap",
};

const tabsRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 0,
  overflow: "visible",
};

const navSiteRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexShrink: 0,
};

const siteNavPos: React.CSSProperties = {
  fontSize: 11,
  color: "var(--sk-color-ink-3, #888)",
  whiteSpace: "nowrap",
  fontFamily: "var(--sk-font-text, sans-serif)",
};

const tabBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "10px 14px",
  fontSize: 13,
  fontFamily: "var(--sk-font-text, sans-serif)",
  whiteSpace: "nowrap",
  textDecoration: "none",
  borderBottom: "2px solid transparent",
  marginBottom: -1,
  transition: "color 0.15s, border-color 0.15s",
};

const activeStyle: React.CSSProperties = {
  color: "var(--sk-color-accent, #0066cc)",
  borderBottomColor: "var(--sk-color-accent, #0066cc)",
  fontWeight: 600,
};

const inactiveStyle: React.CSSProperties = {
  color: "var(--sk-color-ink-2, #555)",
  cursor: "pointer",
};

const lockedStyle: React.CSSProperties = {
  opacity: 0.55,
  cursor: "default",
};

const laterStyle: React.CSSProperties = {
  color: "var(--sk-color-ink-3, #999)",
  cursor: "default",
};

const laterMark: React.CSSProperties = {
  fontSize: 11,
  fontStyle: "italic",
  color: "var(--sk-color-ink-3, #999)",
};

const tierChip: React.CSSProperties = {
  fontSize: 10,
  padding: "1px 5px",
  borderRadius: 3,
  background: "var(--sk-color-surface-2, #f0f0f0)",
  color: "var(--sk-color-ink-3, #888)",
  marginLeft: 4,
};

const countBadge: React.CSSProperties = {
  fontSize: 11,
  color: "var(--sk-color-danger, #c00)",
  fontWeight: 600,
};

const siteNavBtn: React.CSSProperties = {
  fontSize: 12,
  padding: "4px 10px",
  border: "1px solid var(--sk-color-border, #ddd)",
  borderRadius: 4,
  background: "var(--sk-color-surface-1, #fff)",
  color: "var(--sk-color-ink-2, #666)",
  cursor: "pointer",
  fontFamily: "var(--sk-font-text, sans-serif)",
};

const menuStyle: React.CSSProperties = {
  minWidth: 220,
  background: "var(--sk-color-surface-1, #fff)",
  border: "1px solid var(--sk-color-border, #ddd)",
  borderRadius: 6,
  boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
  zIndex: 9999,
  padding: "4px 0",
};

const menuItemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "8px 14px",
  fontSize: 13,
  color: "var(--sk-color-ink-2, #555)",
  textDecoration: "none",
  cursor: "pointer",
  fontFamily: "var(--sk-font-text, sans-serif)",
};

const menuItemActiveStyle: React.CSSProperties = {
  fontWeight: 600,
  color: "var(--sk-color-accent, #0066cc)",
  background: "var(--sk-color-surface-2, #f5f5f5)",
};
